import { useRef, useState, useMemo, useCallback, type ChangeEvent } from 'react'
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Heading,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Select,
  Tabs,
  TabList,
  Tab,
  Badge,
  Text,
  useDisclosure,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  VStack,
} from '@chakra-ui/react'
import { Search } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables'
import { ActionButtons } from '@/components/common/ActionButtons'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useAccessCheck } from '@/hooks/useAccess'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { apiClient } from '@/lib/api/client'
import { AssetCatalogShell } from '@/components/assetCatalog/AssetCatalogShell'
import { useClientContextStore } from '@/store/clientContext'
import { useAssetCatalogPaths } from '@/hooks/useAssetCatalogPaths'
import { downloadCSVTemplate, exportToCSV } from '@/lib/utils/csvUtils'
import { INVENTORY_ASSETS_CSV } from '@/lib/utils/inventoryAssetsCsv'

type TabValue = 'all' | 'trash'

const API_BASE = '/inventory-management/assets'
const ARCHIVED_API = '/inventory-management/archived/assets'

export default function AssetCatalogAssetsPage() {
  const colors = useThemeColors()
  const tableRef = useRef<DataTableRef>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const { showError, showSuccess, showWarning } = useToastNotification()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const activeClientCode = useClientContextStore((s) => s.activeClientCode)
  const { path } = useAssetCatalogPaths()
  const navigate = useNavigate()

  const [activeTab, setActiveTab] = useState<TabValue>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [totalRows, setTotalRows] = useState(0)
  const [totalTrash, setTotalTrash] = useState(0)
  const [bulkAction, setBulkAction] = useState('')
  const [isBulkProcessing, setIsBulkProcessing] = useState(false)
  const [modalMessage, setModalMessage] = useState('')

  const access = useAccessCheck()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isExporting, setIsExporting] = useState(false)
  const [isImporting, setIsImporting] = useState(false)

  const handleDownloadTemplate = useCallback(() => {
    downloadCSVTemplate({
      csvHeaders: [...INVENTORY_ASSETS_CSV.headers],
      exampleRow: [...INVENTORY_ASSETS_CSV.exampleRow],
      comments: [...INVENTORY_ASSETS_CSV.comments],
      filename: INVENTORY_ASSETS_CSV.filename,
      showError,
      showSuccess,
    })
  }, [showError, showSuccess])

  const handleFileUpload = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return
      setIsImporting(true)
      try {
        const form = new FormData()
        form.append('file', file)
        const { data } = await apiClient.post<{
          data: { imported: number; errors: string[] }
        }>(`${API_BASE}/import`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        const d = data?.data ?? { imported: 0, errors: [] }
        showSuccess(
          'Success',
          d.imported > 0 ? `Created ${d.imported} assets` : 'Import completed'
        )
        if (d.errors?.length) {
          showWarning(
            d.errors.slice(0, 3).join('; ') + (d.errors.length > 3 ? '...' : '')
          )
        }
        tableRef.current?.reload()
      } catch (error: unknown) {
        showError(error as Error)
      } finally {
        setIsImporting(false)
        e.target.value = ''
      }
    },
    [showError, showSuccess, showWarning]
  )

  const handleExportData = useCallback(async () => {
    if (activeTab === 'trash') {
      showWarning('Switch to All to export assets')
      return
    }
    setIsExporting(true)
    try {
      await exportToCSV({
        endpoint: API_BASE,
        csvHeaders: [...INVENTORY_ASSETS_CSV.headers],
        dataMapper: (item) => INVENTORY_ASSETS_CSV.mapRow(item as Record<string, unknown>),
        filename: 'assets_export.csv',
        showError,
        showSuccess,
        showWarning,
      })
    } finally {
      setIsExporting(false)
    }
  }, [activeTab, showError, showSuccess, showWarning])

  const tableOptions: DataTableOptions = useMemo(
    () => ({
      showActionsColumn: true,
      dataFields: {
        tag_number: { name: 'Asset Tag', withSort: true, maxWidth: '160px' },
        serial_number: { name: 'Serial Number', withSort: true, maxWidth: '160px' },
        brand_name: { name: 'Brand', withSort: true, maxWidth: '140px' },
        category_name: { name: 'Machine Type', withSort: true, maxWidth: '160px' },
        model: { name: 'Model', withSort: true, maxWidth: '140px' },
        function: { name: 'Function', withSort: false, maxWidth: '140px' },
        active: {
          name: 'Status',
          withSort: true,
          maxWidth: '100px',
          badge: {
            Active: 'green',
            Inactive: 'gray',
            true: 'green',
            false: 'gray',
            '1': 'green',
            '0': 'gray',
          },
          badgeLabels: {
            Active: 'Active',
            Inactive: 'Inactive',
            true: 'Active',
            false: 'Inactive',
            '1': 'Active',
            '0': 'Inactive',
          },
        },
        machine_state: { name: 'Machine State', withSort: false, maxWidth: '120px' },
        updated_at: { name: 'Updated At', withSort: true, maxWidth: '160px' },
      },
      dataSource: activeTab === 'trash' ? ARCHIVED_API : API_BASE,
      primaryKey: 'id',
      softDelete: true,
      displayInModal: false,
      noRecordText: activeClientCode ? 'No assets found' : 'Select a client to load data',
      hideSelection: false,
      otherActions: [],
    }),
    [activeTab, activeClientCode]
  )

  const permissions = useMemo(
    () => ({
      can_edit: access.can_edit,
      can_delete: access.can_delete,
      can_create: access.can_create,
      can_view: access.can_view,
      can_export: access.can_export,
      can_import: access.can_import,
    }),
    [access]
  )

  const tableParams = useMemo(
    () => ({
      search: searchQuery,
      trash: activeTab === 'trash' ? 1 : 0,
      _client: activeClientCode ?? '',
    }),
    [searchQuery, activeTab, activeClientCode]
  )

  const setSubSub = useMemo(
    () => (all: number, trashed: number) => {
      setTotalRows(all)
      setTotalTrash(trashed)
    },
    []
  )

  const handleTabChange = (index: number) => {
    setActiveTab(index === 0 ? 'all' : 'trash')
    setSearchQuery('')
    tableRef.current?.applyFilter('search', '')
    tableRef.current?.clearPage()
  }

  const handleSearch = () => {
    tableRef.current?.applyFilter('search', searchQuery || '')
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch()
  }

  const handleBulkActionClick = () => {
    if (!activeClientCode) {
      showWarning('Select a client first')
      return
    }
    if (!bulkAction) {
      showWarning('Please select an action first')
      return
    }
    const selectedIds = tableRef.current?.getSelectedRows() || []
    if (selectedIds.length === 0) {
      showWarning('Please select at least one item')
      return
    }
    setModalMessage(
      activeTab === 'trash' && bulkAction === 'delete'
        ? 'You are about to permanently delete these assets. This action cannot be undone.'
        : 'Are you sure you want to apply this action?'
    )
    onOpen()
  }

  const handleConfirmBulkAction = async () => {
    const selectedIds = tableRef.current?.getSelectedRows() || []
    if (selectedIds.length === 0) return

    setIsBulkProcessing(true)
    try {
      if (bulkAction === 'restore') {
        await apiClient.post(`${API_BASE}/bulk/restore`, { ids: selectedIds })
      } else if (bulkAction === 'delete' && activeTab === 'trash') {
        await apiClient.post(`${API_BASE}/bulk/force-delete`, { ids: selectedIds })
      } else if (bulkAction === 'delete' && activeTab === 'all') {
        await apiClient.post(`${API_BASE}/bulk/delete`, { ids: selectedIds })
      }
      showSuccess(
        'Success',
        selectedIds.length > 1 ? `${selectedIds.length} assets updated` : 'Updated successfully'
      )
      tableRef.current?.reload()
      setBulkAction('')
      onClose()
    } catch (error: unknown) {
      showError(error as Error)
    } finally {
      setIsBulkProcessing(false)
    }
  }

  const customRenderers = useMemo(
    () => ({
      updated_at: (value: string | null | undefined) => {
        if (!value) return '—'
        try {
          return new Date(value).toLocaleString(undefined, {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
          })
        } catch {
          return String(value)
        }
      },
      tag_number: (value: unknown) => (value == null || value === '' ? '—' : String(value)),
      serial_number: (value: unknown) => (value == null || value === '' ? '—' : String(value)),
      brand_name: (value: unknown) => (value == null || value === '' ? '—' : String(value)),
      category_name: (value: unknown) => (value == null || value === '' ? '—' : String(value)),
      model: (value: unknown) => (value == null || value === '' ? '—' : String(value)),
      function: (value: unknown) => (value == null || value === '' ? '—' : String(value)),
      machine_state: (value: unknown) => (value == null || value === '' ? '—' : String(value)),
    }),
    []
  )

  return (
    <AssetCatalogShell>
      <VStack spacing={2} align="stretch" flex="1" overflow="hidden" minH={0} h="100%">
        <Card flexShrink={0} display="flex" flexDirection="column" flex="1" minH={0} overflow="hidden">
          <CardHeader flexShrink={0} py={3}>
            <Heading size="md" mb={1}>
              Assets
            </Heading>
            <Text fontSize="sm" color={colors.text}>
              Store assets for this client (tag, serial, brand, machine type, model)
            </Text>
          </CardHeader>

          <CardHeader py={0} px={0} flexShrink={0}>
            <Box pt={2} pb={0}>
              <HStack justify="space-between" align="flex-end" spacing={4} px={4} width="100%">
                <Tabs index={activeTab === 'all' ? 0 : 1} onChange={handleTabChange} variant="enclosed">
                  <TabList borderBottom="none">
                    <Tab>
                      All <Badge ml={2} colorScheme="blue">{totalRows}</Badge>
                    </Tab>
                    <Tab>
                      Trash <Badge ml={2} colorScheme="red">{totalTrash}</Badge>
                    </Tab>
                  </TabList>
                </Tabs>
                <Box pb={2}>
                  <ActionButtons
                    pathname={path('assets')}
                    createRoute={path('assets/create')}
                    createLabel="Create Asset"
                    onExport={handleExportData}
                    onDownloadTemplate={handleDownloadTemplate}
                    isExporting={isExporting}
                    isImporting={isImporting}
                    fileInputRef={fileInputRef}
                    onFileUpload={handleFileUpload}
                  />
                </Box>
              </HStack>
              <Box borderBottom="2px" borderColor={colors.border} mx={4} />
            </Box>
          </CardHeader>

          <CardHeader py={1} pt={3} pb={2} flexShrink={0} px={5}>
            <HStack spacing={4} flexWrap="wrap">
              {permissions.can_delete && (
                <HStack spacing={2} minW="250px">
                  <Select
                    placeholder="Bulk actions"
                    size="sm"
                    value={bulkAction}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) => setBulkAction(e.target.value)}
                    isDisabled={!activeClientCode}
                  >
                    {activeTab === 'trash' && <option value="restore">Restore</option>}
                    {activeTab === 'trash' && <option value="delete">Delete Permanently</option>}
                    {activeTab !== 'trash' && <option value="delete">Move to trash</option>}
                  </Select>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    onClick={handleBulkActionClick}
                    isLoading={isBulkProcessing}
                    isDisabled={!activeClientCode}
                  >
                    Apply
                  </Button>
                </HStack>
              )}
              <Box flex="0 1 auto" minW="250px" maxW="400px">
                <InputGroup size="sm">
                  <Input
                    ref={searchRef}
                    placeholder="Search by tag, serial, brand…"
                    value={searchQuery}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    pr="2.5rem"
                    isDisabled={!activeClientCode}
                  />
                  <InputRightElement width="2.5rem">
                    <IconButton
                      aria-label="Search"
                      icon={<Search size={16} />}
                      size="sm"
                      h="1.75rem"
                      w="1.75rem"
                      onClick={handleSearch}
                      variant="ghost"
                      isDisabled={!activeClientCode}
                    />
                  </InputRightElement>
                </InputGroup>
              </Box>
            </HStack>
          </CardHeader>

          <CardBody p={0} pt={1} pb={4} mx={5} flex="1" overflow="hidden" display="flex" flexDirection="column" minH={0}>
            <Box h="100%" display="flex" flexDirection="column" minH={0}>
              {activeClientCode ? (
                <DataTable
                  key={activeClientCode + activeTab}
                  ref={tableRef}
                  options={tableOptions}
                  params={tableParams}
                  setSubSub={setSubSub}
                  onOpenModal={(id) => navigate(path('assets/' + id))}
                  access={permissions}
                  customRenderers={customRenderers}
                />
              ) : (
                <Box py={12} textAlign="center" color={colors.text}>
                  <Text>Select a client to load data.</Text>
                </Box>
              )}
            </Box>
          </CardBody>
        </Card>
      </VStack>
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm</ModalHeader>
          <ModalBody>
            <Text>{modalMessage}</Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleConfirmBulkAction} isLoading={isBulkProcessing}>
              Confirm
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </AssetCatalogShell>
  )
}
